import { Injectable, inject } from '@angular/core';
import { OAuthService, OAuthEvent } from 'angular-oauth2-oidc';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { oauthServerConfig } from './oauth.config';

export interface UserProfile {
  sub?: string;
  name?: string;
  email?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
}

export interface UserInfo {
  info: UserProfile;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private oauthService = inject(OAuthService);
  private router = inject(Router);

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private userInfoSubject = new BehaviorSubject<UserInfo | null>(null);
  private sessionDegradedSubject = new BehaviorSubject<boolean>(false);
  private tokenCheckInterval?: number;
  private tokenMonitoringInterval?: number;

  private readonly USER_INFO_CACHE_KEY = 'caps_user_info_cache';
  /**
   * Durable marker set at sign-out and consumed by the next login. When present,
   * the OAuth authorization request is sent with `prompt=login`, forcing the IdP
   * (PingFederate / CAS) to re-prompt for credentials instead of silently
   * reusing the previous user's still-open IdP session. This is what makes
   * account switching actually work: CAS logout clears the CAS SSO cookie, but
   * the upstream PingFederate session can linger and silently re-authenticate.
   * Deliberately NOT prefixed `caps_` so it survives clearAllAuthStorage().
   */
  private readonly FORCE_REAUTH_KEY = 'force_reauth_on_next_login';

  /** Where the user was headed before being sent to sign in. See rememberRoute(). */
  private static readonly TARGET_ROUTE_KEY = 'caps_targetRoute';
  /**
   * A remembered route older than this is dropped. Someone who abandoned a
   * sign-in an hour ago is not still trying to reach that page.
   */
  private static readonly TARGET_ROUTE_TTL_MS = 10 * 60 * 1000;

  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  public userInfo$ = this.userInfoSubject.asObservable();
  /** True when the access token is valid but the IDP userinfo endpoint returned 401 (opaque token, IDP session gone). */
  public sessionDegraded$ = this.sessionDegradedSubject.asObservable();

  constructor() {
    this.configureAuth();
    this.setupEventHandlers();

    // Initialize with current token state if available
    this.syncAuthenticationState();

    // Start continuous token monitoring
    this.startContinuousTokenMonitoring();
  }

  private configureAuth(): void {
    this.oauthService.configure(oauthServerConfig);
    
    // Use localStorage for cross-tab persistence
    this.oauthService.setStorage(localStorage);
    
    // Disable automatic silent refresh
    // this.oauthService.setupAutomaticSilentRefresh();
    
    // Setup cross-tab communication
    this.setupCrossTabCommunication();
  }

  private syncAuthenticationState(): void {
    // Check if we have valid tokens in localStorage
    const isAuthenticated = this.oauthService.hasValidAccessToken();
    this.isAuthenticatedSubject.next(isAuthenticated);
    
    if (isAuthenticated) {
      // Load user info if we're authenticated
      this.loadUserInfo().catch(error => {
        console.error('Failed to load user info during sync:', error);
        // If we can't load user info, the token might be invalid
        this.isAuthenticatedSubject.next(false);
      });
    }
  }

  private setupCrossTabCommunication(): void {
    // Listen for storage changes (token updates in other tabs)
    window.addEventListener('storage', (event) => {
      if (event.key === 'access_token' || event.key === 'id_token') {
        console.log('🔄 Token changed in another tab, syncing authentication state');
        console.log('🔄 Storage event details:', {
          key: event.key,
          newValue: event.newValue ? 'present' : 'null',
          oldValue: event.oldValue ? 'present' : 'null'
        });
        
        // Force a token validity check with a small delay to ensure localStorage sync
        console.log('🔄 Calling checkTokenValidityAndUpdate from cross-tab sync');
        setTimeout(() => {
          this.checkTokenValidityAndUpdate();
        }, 100);
      }
      
      // Handle logout events from other tabs
      if (event.key === 'logout_event') {
        console.log('🔄 Logout detected from another tab');
        this.handleCrossTabLogout();
      }
      
      // Handle login events from other tabs
      if (event.key === 'login_event') {
        console.log('🔄 Login detected from another tab');
        this.handleCrossTabLogin();
      }
    });
  }

  private handleCrossTabLogout(): void {
    // Stop monitoring
    this.stopContinuousTokenMonitoring();
    
    // Clear local state without triggering another logout event
    this.isAuthenticatedSubject.next(false);
    this.userInfoSubject.next(null);
    
    // Restart monitoring for future logins
    this.startContinuousTokenMonitoring();
    
    // Navigate to login page
    this.router.navigate(['/']);
  }

  private handleCrossTabLogin(): void {
    console.log('🔄 Processing cross-tab login event');
    
    // Force a token validity check which will update auth state if valid
    this.checkTokenValidityAndUpdate();
  }

  private setupEventHandlers(): void {
    // Handle token expiration - try silent refresh first
    this.oauthService.events
      .pipe(filter(e => e.type === 'token_expires'))
      .subscribe((e: OAuthEvent) => {
        console.log('🔴 Token expires event received:', e);
        // console.log('🔄 Attempting silent refresh...');
        
        // this.oauthService.silentRefresh().then(() => {
        //   console.log('✅ Silent refresh successful');
        // }).catch((error) => {
        //   console.error('❌ Silent refresh failed:', error);
        //   console.log('🔴 Logging out due to failed refresh');
        //   this.logout(true); // Logout without redirect to logout URL
        // });
      });

    const oauthEventsSubscription = this.oauthService.events.subscribe((e) => {
      console.log('OAuth Event:', e.type, e);
      
      if (e.type === 'token_received') {
        console.log('Token received, updating auth state');
        this.isAuthenticatedSubject.next(true);
        // Restart continuous monitoring after successful authentication
        this.stopContinuousTokenMonitoring();
        this.startContinuousTokenMonitoring();
        
        // Notify other tabs about successful login
        try {
          localStorage.setItem('login_event', Date.now().toString());
          localStorage.removeItem('login_event');
        } catch (error) {
          console.warn('Could not set login event in localStorage:', error);
        }
        
        // Where to land is NOT decided here. `initLoginFlow()` is called
        // without additional state, so `oauthService.state` is always empty by
        // the time tryLogin() has parsed the callback — the branch that used to
        // live here could only ever fall through to /dashboard, quietly
        // discarding the page the user had asked for. One resolver owns the
        // decision now: LandingService, called from App.ngOnInit once
        // initializeAuth() has settled.
        oauthEventsSubscription.unsubscribe();
      }
      
      // Handle other token expiration events - token_expires is handled separately
      
      if (e.type === 'session_terminated' || e.type === 'session_error') {
        console.log('Session terminated or error');
        this.isAuthenticatedSubject.next(false);
        this.userInfoSubject.next(null);
        oauthEventsSubscription.unsubscribe();
      } else if (e.type === 'invalid_nonce_in_state') {
        // Previously: location.reload(). That produced the browser's
        // "Reload site? Changes you made may not be saved." dialog (it trips
        // the beforeunload guards on profile/user-profile) AND could not fix
        // anything — the stale nonce survives a reload, so the same event
        // re-fired on the next load, prompting repeatedly.
        //
        // The actual remedy is to discard the stale handshake artifacts and
        // scrub the OAuth params out of the URL in place, with no page load.
        console.warn('🟡 invalid_nonce_in_state — clearing stale OAuth handshake state (no reload)');
        oauthEventsSubscription.unsubscribe();
        this.clearStaleOAuthHandshake();
      }
      
      // Handle silent refresh success
      // if (e.type === 'silently_refreshed') {
      //   console.log('✅ Silent refresh successful, updating auth state');
      //   this.isAuthenticatedSubject.next(true);
      //   // Optionally reload user info
      //   this.loadUserInfo().catch(error => 
      //     console.error('Failed to reload user info after silent refresh:', error)
      //   );
      // }
      
      // // Handle silent refresh failures
      // if (e.type === 'silent_refresh_error' || e.type === 'silent_refresh_timeout') {
      //   console.log('❌ Silent refresh failed, user needs to re-authenticate');
      //   this.isAuthenticatedSubject.next(false);
      //   this.userInfoSubject.next(null);
        
      //   // Notify other tabs about authentication failure
      //   try {
      //     localStorage.setItem('logout_event', Date.now().toString());
      //     localStorage.removeItem('logout_event');
      //   } catch (error) {
      //     console.warn('Could not notify other tabs of auth failure:', error);
      //   }
      // }
    });
  }

  async initializeAuth(): Promise<boolean> {
    try {
      console.log('=== AUTH initializeAuth START ===');
      console.log('Initial hasValidAccessToken:', this.oauthService.hasValidAccessToken());
      
      // Try to login if we have tokens
      if (this.oauthService.hasValidAccessToken()) {
        console.log('initializeAuth Try to login if we have tokens',this.oauthService.getIdTokenExpiration(), this.oauthService.getIdToken(),
          this.oauthService.getAccessToken(), this.oauthService.getAccessTokenExpiration());

        console.log('Has valid token, loading user info...');
        await this.loadUserInfo();
        this.isAuthenticatedSubject.next(true);
        // Restart continuous monitoring after successful authentication
        this.stopContinuousTokenMonitoring();
        this.startContinuousTokenMonitoring();
        
        // Notify other tabs about successful auth
        try {
          localStorage.setItem('login_event', Date.now().toString());
          localStorage.removeItem('login_event');
        } catch (error) {
          console.warn('Could not set login event in localStorage:', error);
        }
        
        console.log('Auth successful with existing token');
        console.log('=== AUTH initializeAuth END (existing token) ===');
        return true;
      }

      console.log('No valid token, trying login...');
      // Check if we're returning from OAuth redirect
      await this.oauthService.tryLogin();
      
      console.log('After tryLogin, hasValidAccessToken:', this.oauthService.hasValidAccessToken());
      console.log('initializeAuth',this.oauthService.getIdTokenExpiration(), this.oauthService.getIdToken(),
        this.oauthService.getAccessToken(), this.oauthService.getAccessTokenExpiration());
        
      if (this.oauthService.hasValidAccessToken()) {
        console.log('Token obtained from tryLogin, loading user info...');
        await this.loadUserInfo();
        this.isAuthenticatedSubject.next(true);
        // Restart continuous monitoring after successful authentication
        this.stopContinuousTokenMonitoring();
        this.startContinuousTokenMonitoring();
        
        // Notify other tabs about successful auth
        try {
          localStorage.setItem('login_event', Date.now().toString());
          localStorage.removeItem('login_event');
        } catch (error) {
          console.warn('Could not set login event in localStorage:', error);
        }
        
        console.log('Auth successful with new token');
        console.log('=== AUTH initializeAuth END (new token) ===');
        return true;
      }

      console.log('No authentication possible');
      console.log('=== AUTH initializeAuth END (no auth) ===');
      return false;
    } catch (error) {
      console.error('Auth initialization error:', error);
      console.log('=== AUTH initializeAuth END (error) ===');
      return false;
    }
  }

  async login(): Promise<void> {
    try {
      console.log('=== AUTH login() called ===');
      console.log('Starting OAuth login flow...');

      // If we just signed out, force the IdP to re-prompt for credentials so a
      // lingering upstream (PingFederate) session can't silently restore the
      // previous user. Consume the marker so normal logins stay seamless.
      let forceReauth = false;
      try {
        forceReauth = localStorage.getItem(this.FORCE_REAUTH_KEY) === '1';
        if (forceReauth) {
          localStorage.removeItem(this.FORCE_REAUTH_KEY);
        }
      } catch { /* storage unavailable — fall back to default flow */ }

      if (forceReauth) {
        console.log('🔐 Forcing credential re-entry (prompt=login) after sign-out');
        this.oauthService.initLoginFlow('', { prompt: 'login' });
      } else {
        this.oauthService.initLoginFlow();
      }

      console.log('OAuth login flow initiated');
    } catch (error) {
      console.error('Login error:', error);
    }
  }

  /**
   * Logout: clears all local tokens/state and terminates the SSO session
   * via a hidden iframe (no full-page redirect).
   */
  logout(): void {
    console.log('🟠 AuthService.logout() called');

    // 1. Stop monitoring
    this.stopContinuousTokenMonitoring();

    // 2. Notify other tabs
    try {
      localStorage.setItem('logout_event', Date.now().toString());
      localStorage.removeItem('logout_event');
    } catch (error) {
      console.warn('Could not set logout event in localStorage:', error);
    }

    // 3. Nuke ALL auth-related storage (localStorage + sessionStorage)
    this.clearAllAuthStorage();

    // 3b. Mark the next login so it re-prompts for credentials. Set AFTER
    // clearAllAuthStorage() so it survives the wipe. Without this, CAS logout
    // alone leaves the PingFederate session intact and the next sign-in
    // silently restores the previous user.
    try {
      localStorage.setItem(this.FORCE_REAUTH_KEY, '1');
    } catch (error) {
      console.warn('Could not set force-reauth marker:', error);
    }

    // 4. Update internal state
    this.isAuthenticatedSubject.next(false);
    this.userInfoSubject.next(null);
    this.sessionDegradedSubject.next(false);

    // 5. Full-page redirect to CAS logout. CAS clears its SSO cookie and
    // 302s back to the `service` URL (configured as our app baseUrl in
    // oauth.config.ts). The hidden-iframe approach was unreliable: CAS sets
    // X-Frame-Options which blocks iframe loads, so the SSO session was
    // never actually terminated and re-login silently restored the prior
    // user. A top-level navigation is the only way to guarantee the CAS
    // session cookie is cleared.
    const logoutUrl = oauthServerConfig.logoutUrl;
    if (logoutUrl) {
      console.log('🟠 Redirecting to CAS logout URL:', logoutUrl);
      window.location.href = logoutUrl;
    } else {
      console.warn('🟠 No logoutUrl configured, falling back to local navigation');
      this.router.navigate(['/']);
    }
  }

  /**
   * Soft session-expiry handling: clears local auth state so no further API
   * calls go out with a dead token, but does NOT redirect to CAS logout and
   * does NOT navigate away. The user stays on whatever page/form they were
   * working on and sees the "session expired" snackbar with a "Log In"
   * button (wired up in app.ts). Contrast with logout(), which is the
   * explicit, disruptive Sign-Out flow (full-page CAS redirect) — that
   * should only run when the user deliberately signs out or switches
   * accounts, not when a token simply times out mid-session.
   */
  handleSessionExpired(): void {
    console.log('🟡 AuthService.handleSessionExpired() called (soft — no redirect, no navigation)');

    this.stopContinuousTokenMonitoring();
    this.clearAllAuthStorage();

    this.isAuthenticatedSubject.next(false);
    this.userInfoSubject.next(null);
    this.sessionDegradedSubject.next(false);
  }

  private async loadUserInfo(): Promise<void> {
    try {
      const userInfo = await this.oauthService.loadUserProfile();
      const wrappedUserInfo = userInfo as UserInfo;
      this.userInfoSubject.next(wrappedUserInfo);
      this.sessionDegradedSubject.next(false);
      try {
        localStorage.setItem(this.USER_INFO_CACHE_KEY, JSON.stringify(wrappedUserInfo));
      } catch { /* storage quota exceeded — non-fatal */ }
    } catch (error: any) {
      console.error('Error loading user info:', error);
      // 401 from userinfo = opaque token whose IDP session expired while the
      // access token itself is still valid for resource-server calls.
      // Restore from cache so the header can still show who is logged in.
      const cached = this.getCachedUserInfo();
      if (cached) {
        console.log('ℹ️ Userinfo endpoint unavailable — restoring cached user identity');
        this.userInfoSubject.next(cached);
        this.sessionDegradedSubject.next(true);
      } else {
        this.userInfoSubject.next(null);
        this.sessionDegradedSubject.next(true);
      }
    }
  }

  private getCachedUserInfo(): UserInfo | null {
    try {
      const raw = localStorage.getItem(this.USER_INFO_CACHE_KEY);
      return raw ? (JSON.parse(raw) as UserInfo) : null;
    } catch {
      return null;
    }
  }

  isAuthenticated(): boolean {
    // Use the BehaviorSubject as the single source of truth
    // The continuous monitoring handles all token validation logic
    const authState = this.isAuthenticatedSubject.value;
    
    console.log('🔍 isAuthenticated() called, returning auth state:', authState);
    
    return authState;
  }

  getAccessToken(): string | null {
    return this.oauthService.getAccessToken();
  }

  getUserInfo(): UserInfo | null {
    return this.userInfoSubject.value;
  }

  getUserEmail(): string | null {
    const userInfo = this.getUserInfo();
    return userInfo?.info?.email || null;
  }

  getUserName(): string | null {
    const userInfo = this.getUserInfo();
    return userInfo?.info?.name || null;
  }

  /**
   * Remember where the user was trying to go, so the sign-in round trip can
   * put them back there. Written when the auth guard turns someone away and
   * when a session expires under them; read exactly once, by the landing
   * resolver.
   *
   * Some URLs must never be remembered:
   *  - the Impersonate picker, which is opt-in and never a landing place;
   *  - /unauthorized, which is a refusal, not a destination;
   *  - the OAuth callback (/index.html?code=…), which would loop;
   *  - the root, which is what "no particular page" already means.
   */
  rememberRoute(url: string): void {
    if (!AuthService.isRememberableRoute(url)) return;
    try {
      sessionStorage.setItem(
        AuthService.TARGET_ROUTE_KEY,
        JSON.stringify({ url, storedAt: Date.now() }),
      );
    } catch { /* storage unavailable — the user simply lands on home */ }
  }

  /**
   * Take the remembered route, if there is a usable one. Always clears it:
   * a route is offered once, so an abandoned sign-in cannot resurface days
   * later and yank a working session onto a stale page.
   */
  consumeRememberedRoute(): string | null {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(AuthService.TARGET_ROUTE_KEY);
      sessionStorage.removeItem(AuthService.TARGET_ROUTE_KEY);
    } catch { return null; }
    if (!raw) return null;

    try {
      const { url, storedAt } = JSON.parse(raw) as { url?: string; storedAt?: number };
      if (!url || !AuthService.isRememberableRoute(url)) return null;
      if (!storedAt || Date.now() - storedAt > AuthService.TARGET_ROUTE_TTL_MS) {
        console.log('🔖 Remembered route has gone stale — landing on home instead');
        return null;
      }
      return url;
    } catch {
      return null;
    }
  }

  private static isRememberableRoute(url: string): boolean {
    if (!url) return false;
    const path = url.split('?')[0].split('#')[0];
    if (path === '' || path === '/') return false;
    if (/^\/?(impersonate|auth)\b/.test(path)) return false;
    if (path === '/unauthorized') return false;
    if (/index\.html$/.test(path)) return false;
    if (/[?&](code|state)=/.test(url)) return false;
    return true;
  }

  // Get token expiry time in milliseconds since 1970
  getTokenExpiry(): number | null {
    try {
      const accessTokenExpiration = this.oauthService.getAccessTokenExpiration();
      console.log('getTokenExpiry - accessTokenExpiration (ms):', accessTokenExpiration);
      
      if (accessTokenExpiration) {
        return accessTokenExpiration;
      }
      
      console.log('getTokenExpiry - no expiration found');
      return null;
    } catch (error) {
      console.warn('Error getting token expiry:', error);
      return null;
    }
  }

  // Check if token is currently valid
  hasValidToken(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  // Get time until token expires (in milliseconds)
  getTimeUntilExpiry(): number | null {
    const expiry = this.getTokenExpiry();
    if (!expiry) return null;
    
    const now = Date.now();
    const timeUntilExpiry = expiry - now;
    return timeUntilExpiry > 0 ? timeUntilExpiry : 0;
  }

  private startContinuousTokenMonitoring(): void {
    // Check token validity every 30 seconds
    this.tokenMonitoringInterval = window.setInterval(() => {
      this.checkTokenValidityAndUpdate();
    }, 30000); // 30 seconds

    console.log('🟢 Started continuous token monitoring (30s interval)');
  }

  private stopContinuousTokenMonitoring(): void {
    if (this.tokenMonitoringInterval) {
      clearInterval(this.tokenMonitoringInterval);
      this.tokenMonitoringInterval = undefined;
      console.log('🔴 Stopped continuous token monitoring');
    }
  }

  private checkTokenValidityAndUpdate(): void {
    // console.log('🔄 checkTokenValidityAndUpdate() called');
    const hasAccessToken = !!this.oauthService.getAccessToken();
    const accessTokenExpiry = this.oauthService.getAccessTokenExpiration();
    const currentTime = Date.now();
    const isTokenExpired = accessTokenExpiry ? currentTime >= accessTokenExpiry : true;
    const currentAuthState = this.isAuthenticatedSubject.value;
    
    // console.log('🔍 Cross-tab token check:', {
    //   hasAccessToken,
    //   accessTokenExpiry: accessTokenExpiry ? new Date(accessTokenExpiry).toISOString() : 'none',
    //   currentTime: new Date(currentTime).toISOString(),
    //   isTokenExpired,
    //   currentAuthState,
    //   timeDiff: accessTokenExpiry ? (accessTokenExpiry - currentTime) / 1000 : 'N/A'
    // });

    // If we have a token but it's expired, clear authentication state
    if (hasAccessToken && isTokenExpired) {
      console.log('🔴 Continuous monitoring detected expired token, clearing auth state');
      this.clearAuthenticationState();
      return;
    }

    // If we have a valid token but auth state is false, update it
    if (hasAccessToken && !isTokenExpired && !this.isAuthenticatedSubject.value) {
      console.log('✅ Cross-tab sync: detected valid token, updating auth state to true');
      this.isAuthenticatedSubject.next(true);
      // Reload user info if we don't have it
      if (!this.userInfoSubject.value) {
        console.log('✅ Cross-tab sync: loading user info');
        this.loadUserInfo().catch(err => 
          console.error('Failed to load user info during cross-tab sync:', err)
        );
      }
      return;
    }

    // If we don't have a token but auth state is true, clear it
    if (!hasAccessToken && this.isAuthenticatedSubject.value) {
      console.log('❌ Continuous monitoring detected missing token, clearing auth state');
      this.clearAuthenticationState();
      return;
    }
  }

  private clearAuthenticationState(): void {
    console.log('🧹 Clearing authentication state');

    // Stop token monitoring to prevent loops
    this.stopContinuousTokenMonitoring();

    // Clear internal state first
    this.isAuthenticatedSubject.next(false);
    this.userInfoSubject.next(null);
    this.sessionDegradedSubject.next(false);

    // Clear tokens from all storage
    this.clearAllAuthStorage();

    // Notify other tabs
    try {
      localStorage.setItem('logout_event', Date.now().toString());
      localStorage.removeItem('logout_event');
    } catch (error) {
      console.warn('Could not set logout event in localStorage:', error);
    }

    // Navigate to root while preserving browser history
    this.navigateToRootWithHistory();

    // Restart token monitoring for future logins
    this.startContinuousTokenMonitoring();
  }

  /**
   * Discard a half-finished OAuth handshake (stale nonce / PKCE verifier /
   * state) without touching a valid session and without reloading the page.
   *
   * Only the handshake keys are cleared — never `access_token`/`expires_at` —
   * so an already-authenticated user keeps working uninterrupted. The OAuth
   * query params are stripped via history.replaceState so a later refresh
   * doesn't replay the bad callback.
   */
  private clearStaleOAuthHandshake(): void {
    for (const key of ['nonce', 'PKCE_verifier', 'session_state']) {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }

    try {
      const url = new URL(window.location.href);
      let touched = false;
      for (const param of ['code', 'state', 'session_state', 'error']) {
        if (url.searchParams.has(param)) {
          url.searchParams.delete(param);
          touched = true;
        }
      }
      if (touched) {
        window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
      }
    } catch (error) {
      console.warn('Could not scrub OAuth params from URL:', error);
    }

    // If the existing token is still good, the user stays signed in and never
    // notices. Otherwise surface the normal signed-out state — the expired
    // session snackbar handles re-login without yanking them off the page.
    if (!this.oauthService.hasValidAccessToken()) {
      this.isAuthenticatedSubject.next(false);
    }
  }

  /** Remove ALL auth-related keys from both localStorage and sessionStorage */
  private clearAllAuthStorage(): void {
    // Keys used by angular-oauth2-oidc (matches library's own logOut cleanup)
    const oauthKeys = [
      'access_token',
      'id_token',
      'refresh_token',
      'expires_at',
      'id_token_expires_at',
      'id_token_stored_at',
      'id_token_claims_obj',
      'access_token_stored_at',
      'nonce',
      'PKCE_verifier',
      'granted_scopes',
      'session_state',
    ];

    // Clear from both storages
    for (const key of oauthKeys) {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }

    // Also clear any CAPS-specific persisted state (including user info cache)
    const capsKeys = Object.keys(localStorage).filter(k => k.startsWith('caps_'));
    for (const key of capsKeys) {
      localStorage.removeItem(key);
    }
    localStorage.removeItem(this.USER_INFO_CACHE_KEY);

    console.log('🧹 Cleared all auth storage (localStorage + sessionStorage)');
  }

  private navigateToRootWithHistory(): void {
    console.log('🏠 Navigating to root while preserving browser history');
    // Use Angular Router navigate to maintain browser history stack
    // This ensures the back button will work to return to the previous route
    this.router.navigate(['/']);
  }
}